import { useCallback } from "react";
import { TellescopeModelFromName, useTellescopeQueryClient } from "@tellescope/tanstack-query";
import { useNavigate } from "react-router-dom";
import { buildMessagingThreadLink } from "../utils/build-messaging-thread-link";
import { useUpdateEnduserInSidebar } from "./useUpdateEnduserInSidebar";

/**
 * Hook that returns a function to open a Chat thread for a given chatroom.
 *
 * This hook:
 * 1. Gets the queryClient and navigate internally
 * 2. Returns a callback that fetches the thread and navigates to it
 * 3. Extracts the roomId from the chat message
 * 4. Queries for the matching thread using load_threads with filters
 * 5. Navigates to the thread URL if found
 *
 * @returns A function that accepts a chatroom record and opens its thread
 */
export const useOpenChatThread = () => {
  const queryClient = useTellescopeQueryClient();
  const navigate = useNavigate();
  const updateEnduserInSidebar = useUpdateEnduserInSidebar();

  const openChatThread = useCallback(
    async (chatroom: TellescopeModelFromName<"chat_rooms">, draftId?: string): Promise<void> => {
      try {
        // Fetch the thread using load_threads
        const result = await queryClient.fetchInfiniteQuery({
          model: "inbox_threads",
          method: "load_threads",
          request: {
            autobuild: true,
            limit: 1,
            mdbFilter: {
              type: "Chat",
              threadId: chatroom.id,
            },
          },
          getNextPageParam: (lastPage) => lastPage.threads.at(-1)?.timestamp,
          initialPageParam: undefined,
          paginateRequest: (lastTimestamp, request) => ({ ...request, lastTimestamp }),
        });

        // Extract the first thread from the result
        const thread = result.pages[0]?.threads[0];

        if (!thread) {
          console.warn("No thread found for chat message:", chatroom.id);
          return;
        }

        // Navigate to the thread
        const threadLink = buildMessagingThreadLink({
          view: "inbox",
          channel: "Chat",
          threadId: thread.id,
          draftId,
        });

        const enduserId = thread.enduserIds?.[0];
        updateEnduserInSidebar(enduserId);

        navigate(threadLink);
      } catch (error) {
        console.error("Error opening chat thread:", error);
      }
    },
    [navigate, queryClient, updateEnduserInSidebar]
  );

  return openChatThread;
};
